import { Controller } from '@hotwired/stimulus'
import {
  setupShelfMarquee,
  pauseShelfMarquee,
  resumeShelfMarquee,
  prefersReducedMotion
} from '../utils/shelf_marquee'

// Horizontal shelf/category tabs.
// After Turbo Frame refresh, restore the previous scroll position and only
// re-center when the active tab is outside the visible area.
// Optional autoplay (step-by-step) or marquee (continuous smooth scroll).
export default class extends Controller {
  static targets = ['track', 'item']
  static values = {
    persistKey: { type: String, default: 'category-carousel' },
    autoplay: { type: Boolean, default: false },
    marquee: { type: Boolean, default: false },
    marqueeDuration: { type: Number, default: 30 },
    interval: { type: Number, default: 2000 }
  }

  connect () {
    if (!this.hasTrackTarget) return

    if (this.marqueeValue) {
      this.connectMarquee()
      return
    }

    this.boundPersist = () => this.persistScroll()
    this.pauseAutoplay = () => this.stop()
    this.resumeAutoplay = () => this.start()
    this.resumeAfterInteraction = () => this.scheduleResume()

    this.trackTarget.addEventListener('click', this.boundPersist, true)
    this.trackTarget.addEventListener('scroll', this.boundPersist, { passive: true })

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.restoreOrRevealActive())
    })

    if (!this.canAutoplay) return

    this.element.addEventListener('mouseenter', this.pauseAutoplay)
    this.element.addEventListener('mouseleave', this.resumeAutoplay)
    this.trackTarget.addEventListener('touchstart', this.pauseAutoplay, { passive: true })
    this.trackTarget.addEventListener('touchend', this.resumeAfterInteraction, { passive: true })
    this.start()
  }

  connectMarquee () {
    this.pauseMarquee = () => pauseShelfMarquee(this.trackTarget)
    this.resumeMarquee = () => resumeShelfMarquee(this.trackTarget)

    setupShelfMarquee(this.trackTarget, {
      durationSeconds: this.marqueeDurationValue,
      reducedMotion: this.reducedMotion
    })

    this.element.classList.add('shelf-marquee')
    this.element.addEventListener('mouseenter', this.pauseMarquee)
    this.element.addEventListener('mouseleave', this.resumeMarquee)
    this.trackTarget.addEventListener('touchstart', this.pauseMarquee, { passive: true })
    this.trackTarget.addEventListener('touchend', this.resumeMarquee, { passive: true })
  }

  disconnect () {
    this.stop()
    this.clearResumeTimer()

    if (!this.hasTrackTarget) return

    if (this.marqueeValue) {
      this.element.removeEventListener('mouseenter', this.pauseMarquee)
      this.element.removeEventListener('mouseleave', this.resumeMarquee)
      this.trackTarget.removeEventListener('touchstart', this.pauseMarquee)
      this.trackTarget.removeEventListener('touchend', this.resumeMarquee)
      return
    }

    this.trackTarget.removeEventListener('click', this.boundPersist, true)
    this.trackTarget.removeEventListener('scroll', this.boundPersist)
    this.element.removeEventListener('mouseenter', this.pauseAutoplay)
    this.element.removeEventListener('mouseleave', this.resumeAutoplay)
    this.trackTarget.removeEventListener('touchstart', this.pauseAutoplay)
    this.trackTarget.removeEventListener('touchend', this.resumeAfterInteraction)
  }

  prev (event) {
    event?.preventDefault()
    if (this.marqueeValue) return

    if (this.canAutoplay) {
      this.stop()
      this.scrollToAdjacent(-1, true)
      this.scheduleResume()
      return
    }
    this.scrollToAdjacent(-1)
  }

  next (event) {
    event?.preventDefault()
    if (this.marqueeValue) return

    if (this.canAutoplay) {
      this.stop()
      this.scrollToAdjacent(1, true)
      this.scheduleResume()
      return
    }
    this.scrollToAdjacent(1)
  }

  restoreOrRevealActive () {
    const track = this.trackTarget
    if (!track) return

    const saved = this.readSavedScroll()
    if (saved != null) {
      this.setScrollLeft(saved)
    }

    const active = this.itemTargets.find((el) => el.dataset.categoryCarouselActive === 'true')
    if (!active) return

    if (this.isItemVisible(active)) return

    this.scrollItemIntoTrackCenter(active, 'auto')
  }

  persistScroll () {
    if (!this.hasTrackTarget) return
    try {
      sessionStorage.setItem(this.storageKey(), String(Math.round(this.trackTarget.scrollLeft)))
    } catch (_error) {
      // Ignore private-mode / quota errors.
    }
  }

  readSavedScroll () {
    try {
      const raw = sessionStorage.getItem(this.storageKey())
      if (raw == null || raw === '') return null
      const value = Number(raw)
      return Number.isFinite(value) ? value : null
    } catch (_error) {
      return null
    }
  }

  storageKey () {
    return `ebazzari:category-carousel:${this.persistKeyValue}:${window.location.pathname}`
  }

  setScrollLeft (left) {
    const track = this.trackTarget
    const previous = track.style.scrollBehavior
    track.style.scrollBehavior = 'auto'
    track.scrollLeft = Math.max(0, left)
    track.style.scrollBehavior = previous
  }

  isItemVisible (item) {
    const track = this.trackTarget
    if (!track || !item) return false

    const trackRect = track.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    const pad = 8

    return itemRect.left >= trackRect.left - pad && itemRect.right <= trackRect.right + pad
  }

  scrollToAdjacent (direction, loop = false) {
    const items = this.itemTargets
    if (!items.length) return

    const track = this.trackTarget
    const trackCenter = track.getBoundingClientRect().left + track.clientWidth / 2

    let currentIndex = 0
    let minDistance = Infinity

    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect()
      const itemCenter = rect.left + rect.width / 2
      const distance = Math.abs(itemCenter - trackCenter)
      if (distance < minDistance) {
        minDistance = distance
        currentIndex = index
      }
    })

    let nextIndex = currentIndex + direction
    if (loop) {
      nextIndex = (nextIndex + items.length) % items.length
    } else {
      nextIndex = Math.max(0, Math.min(items.length - 1, nextIndex))
    }

    this.scrollItemIntoTrackCenter(items[nextIndex], 'smooth')
    this.persistScroll()
  }

  scrollItemIntoTrackCenter (item, behavior = 'smooth') {
    const track = this.trackTarget
    if (!track || !item) return

    const itemCenter = item.offsetLeft + item.offsetWidth / 2
    const scrollLeft = itemCenter - track.clientWidth / 2

    track.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior
    })
    this.persistScroll()
  }

  advance () {
    if (!this.canAutoplay) return
    this.scrollToAdjacent(1, true)
  }

  start () {
    if (!this.canAutoplay) return

    this.stop()
    this.timer = window.setInterval(() => this.advance(), this.intervalValue)
  }

  stop () {
    if (!this.timer) return

    window.clearInterval(this.timer)
    this.timer = null
  }

  scheduleResume () {
    if (!this.canAutoplay) return

    this.clearResumeTimer()
    this.resumeTimer = window.setTimeout(() => this.start(), this.intervalValue)
  }

  clearResumeTimer () {
    if (!this.resumeTimer) return

    window.clearTimeout(this.resumeTimer)
    this.resumeTimer = null
  }

  get reducedMotion () {
    return prefersReducedMotion()
  }

  get canAutoplay () {
    return this.autoplayValue && this.itemTargets.length > 1 && !this.reducedMotion
  }
}
